import type { QuoteData } from '@better-lights/core';
import { useAccount } from '../account/store.ts';
import { apiMessage } from '../api/errors.ts';
import { downloadBlob } from '../export/save.ts';
import { joinList, plural } from '../lib/format.ts';
import { handleFeatureError, openPaywallFor } from '../plans/gate.ts';
import { quoteDataOf } from '../quote/model.ts';
import { services } from '../services/index.ts';
import { flushAutosave } from '../state/autosave.ts';
import { projectState, useDocument } from '../state/documentStore.ts';
import { ask, errorMessage, toast } from '../state/feedback.ts';
import { BACKUP_EXTENSION, exportBackup, importBackup } from '../storage/backup.ts';
import { shaOfUri } from '../storage/blobStore.ts';
import { db } from '../storage/db.ts';
import { requestPersistence, storage } from '../storage/index.ts';
import { fileFromState, type ProjectFile, projectTitle } from '../storage/record.ts';
import { syncHooks } from '../sync/hooks.ts';
import { canSyncScope } from '../sync/service.ts';

/** Shared delete confirmation (docs/spec/06 §4.1). Returns true when deleted. */
export async function confirmDeleteProject(file: ProjectFile): Promise<boolean> {
  const parts = ['The photo', 'drawing'];
  if (file.aiResults.length) parts.push(plural(file.aiResults.length, 'render'));
  if (file.exports.length) parts.push(plural(file.exports.length, 'export'));
  const ok = await ask({
    title: `Delete “${projectTitle(file)}”?`,
    message: `${joinList(parts)} will be removed from Better Lights. Copies already saved to Photos stay there.`,
    actions: [
      { label: 'Delete Project', value: true, variant: 'danger' },
      { label: 'Cancel', value: false, variant: 'ghost' },
    ],
  });
  if (!ok) return false;
  if (useDocument.getState().projectId === file.id) {
    await flushAutosave();
    useDocument.getState().close();
  }
  await storage().projects.delete(file.id);
  return true;
}

/** Downloads a `.betterlights` backup of the given projects. */
export async function backupProjects(files: ProjectFile[], name: string): Promise<void> {
  try {
    await flushAutosave();
    const blob = await exportBackup(files, storage().blobs);
    downloadBlob(blob, `${name}${BACKUP_EXTENSION}`);
    toast('Backup downloaded', 'success');
  } catch (e) {
    toast(errorMessage(e, 'Could not create the backup.'), 'error');
  }
}

/** Imports a `.betterlights` backup (or project .json). Returns the imported projects. */
export async function importProjects(file: File): Promise<ProjectFile[]> {
  try {
    const { projects, blobs } = storage();
    const existing = new Set((await projects.list()).map((p) => p.id));
    const result = await importBackup(file, blobs, existing);
    for (const p of result.projects) {
      await projects.save(p);
      syncHooks.projectCreated(p.id);
    }
    void requestPersistence();
    toast(`${plural(result.projects.length, 'project')} imported`, 'success');
    return result.projects;
  } catch (e) {
    toast(errorMessage(e, 'Could not import that file.'), 'error');
    return [];
  }
}

/** Opens a native file picker. */
export function pickFile(accept: string, capture?: 'environment'): Promise<File | null> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = accept;
    if (capture) input.setAttribute('capture', capture);
    input.style.display = 'none';
    let done = false;
    const finish = (f: File | null) => {
      if (done) return;
      done = true;
      input.remove();
      resolve(f);
    };
    input.addEventListener('change', () => finish(input.files?.[0] ?? null));
    input.addEventListener('cancel', () => finish(null));
    document.body.appendChild(input);
    input.click();
  });
}

/** Deletes an AI render or export from a project (open or not) and its file if unreferenced. */
export async function deleteProjectFile(projectId: string, kind: 'ai' | 'export', fileId: string): Promise<void> {
  const doc = useDocument.getState();
  if (doc.projectId === projectId) {
    doc.dispatch(kind === 'ai' ? { type: 'removeAiResult', id: fileId } : { type: 'removeExport', id: fileId });
    await flushAutosave();
    return;
  }
  const { projects } = storage();
  const file = await projects.get(projectId);
  if (!file) return;
  const list = kind === 'ai' ? file.aiResults : file.exports;
  const gone = list.find((x) => x.id === fileId);
  const next: ProjectFile =
    kind === 'ai'
      ? { ...file, aiResults: file.aiResults.filter((x) => x.id !== fileId), updatedAt: Date.now() }
      : { ...file, exports: file.exports.filter((x) => x.id !== fileId), updatedAt: Date.now() };
  await projects.save(next);
  syncHooks.projectSaved(projectId);
  const sha = gone ? shaOfUri(gone.uri) : null;
  if (sha) await projects.collect([sha]);
}

/**
 * Updates the project's `quote` data (docs/spec/08 §3). When the project is open in the editor the
 * edit goes through the open file (so autosave never overwrites it with a stale copy).
 */
export async function updateQuote(projectId: string, patch: Partial<QuoteData>): Promise<void> {
  const doc = useDocument.getState();
  if (doc.projectId === projectId && doc.file) {
    await flushAutosave();
    const file = useDocument.getState().file!;
    const next = fileFromState(projectState(), { ...file, quote: { ...quoteDataOf(file), ...patch } }, Date.now());
    useDocument.getState().setFile(next);
    await storage().projects.save(next);
  } else {
    const file = await storage().projects.get(projectId);
    if (!file) return;
    await storage().projects.save({ ...file, quote: { ...quoteDataOf(file), ...patch }, updatedAt: Date.now() });
  }
  syncHooks.projectSaved(projectId);
}

/** Moves a project to another workspace (personal ↔ company). Local-only projects are simply backed up there. */
export async function moveProjectFlow(projectId: string): Promise<void> {
  const { me, activeScope } = useAccount.getState();
  if (!me) return;
  const row = await db().projectSync.get(projectId);
  const from = row?.scope ?? activeScope ?? me.personalScope;
  const targets = [{ id: me.personalScope, name: 'Personal' }, ...me.orgs.filter((o) => o.role !== 'viewer')].filter(
    (t) => t.id !== from,
  );
  if (!targets.length) return;
  const to = await ask({
    title: 'Move to which workspace?',
    message: 'The project keeps its history. People in that workspace will see it.',
    actions: [
      ...targets.map((t) => ({ label: t.name, value: t.id as string | null, variant: 'secondary' as const })),
      { label: 'Cancel', value: null, variant: 'ghost' as const },
    ],
  });
  if (!to) return;
  // Workspaces are cloud-backed: a workspace without sync (Free, or a company without a Team plan)
  // can't receive it (the server answers 402 feature_required too).
  if (!canSyncScope(to)) {
    if (to === me.personalScope) openPaywallFor('sync');
    else openPaywallFor('sync', 'team', to);
    return;
  }
  try {
    if (row) await services.sync.moveProject(projectId, to);
    else await services.sync.backup([projectId], to);
    services.account.setActiveScope(to);
    toast(`Moved to ${targets.find((t) => t.id === to)?.name}`, 'success');
  } catch (e) {
    if (!handleFeatureError(e)) toast(apiMessage(e, 'Could not move the project.'), 'error');
  }
}
